<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import authV2ForgotPasswordIllustrationDark from '@images/pages/auth-v2-forgot-password-illustration-dark.png'
import authV2ForgotPasswordIllustrationLight from '@images/pages/auth-v2-forgot-password-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useAuthStore } from '@core/stores/AuthStore'

const authThemeImg = useGenerateImageVariant(authV2ForgotPasswordIllustrationLight, authV2ForgotPasswordIllustrationDark)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const i18n = useI18n()

const email = ref('')
const errorMessage = ref('')
const isFormSubmitting = ref(false)
const isSubmissionSuccessful = ref(false)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const { recaptchaLoaded, executeRecaptcha } = useReCaptcha()
const authStore = useAuthStore()

async function executeForgotPasswordRecaptcha() {
  await recaptchaLoaded()

  return executeRecaptcha('forgot_password')
}

async function requestPasswordReset() {
  isFormSubmitting.value = true
  isSubmissionSuccessful.value = false

  try {
    const recaptchaToken = await executeForgotPasswordRecaptcha()

    await authStore.forgotPassword({
      email: email.value,
      recaptchaToken,
    })

    isSubmissionSuccessful.value = true
  } catch (err) {
    console.error(err)

    if (err.response?.error) {
      errorMessage.value = err.response.error.message
    } else {
      errorMessage.value = i18n.t('Something went wrong')
    }
  } finally {
    isFormSubmitting.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper bg-surface"
    no-gutters
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 150px;"
        >
          <VImg
            max-width="468"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ $t('Forgot Password?') }} 🔒
          </h4>
          <p class="mb-0">
            {{ $t('Enter your email and we\'ll send you instructions to reset your password') }}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="requestPasswordReset">
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="email"
                  autofocus
                  label="Email"
                  type="email"
                  placeholder="johndoe@email.com"
                  :disabled="isFormSubmitting"
                />
              </VCol>

              <!-- Reset link -->
              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :disabled="isFormSubmitting"
                >
                  {{ $t('Send Reset Link') }}
                </VBtn>
              </VCol>

              <VAlert
                v-if="isSubmissionSuccessful"
                :title="$t('Success')"
                :text="$t('Forgot password mail sent')"
              />
              <ErrorMessage :message="errorMessage" />

              <!-- back to login -->
              <VCol cols="12">
                <RouterLink
                  class="d-flex align-center justify-center"
                  :to="{ name: 'login' }"
                >
                  <VIcon
                    icon="tabler-chevron-left"
                    size="20"
                    class="me-1 flip-in-rtl"
                  />
                  <span>{{ $t('Back to login') }}</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
